import { useState, useEffect, useRef, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Send, X, Sparkles } from 'lucide-react';
import { AppIcon } from './AppLogo';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Routes where Nova should NOT appear (pre-login / public pages)
const HIDDEN_PATHS = ['/', '/login', '/about', '/services', '/how-it-works', '/lead', '/get-started', '/auth-callback', '/accept-beneficiary'];

// Tiny markdown-ish renderer: **bold**, `code`, and line breaks. Safe (no raw HTML).
function FormattedText({ text }) {
  const renderInline = (line, lineKey) => {
    const parts = line.split(/(\*\*[^*]+\*\*|`[^`]+`)/g).filter(Boolean);
    return parts.map((part, i) => {
      if (part.startsWith('**') && part.endsWith('**')) {
        return <strong key={`${lineKey}-${i}`} className="font-semibold text-gray-900">{part.slice(2, -2)}</strong>;
      }
      if (part.startsWith('`') && part.endsWith('`')) {
        return <code key={`${lineKey}-${i}`} className="px-1.5 py-0.5 rounded bg-[#EBF0F5] text-[#1E3A63] text-[0.8em] font-mono">{part.slice(1, -1)}</code>;
      }
      return <span key={`${lineKey}-${i}`}>{part}</span>;
    });
  };
  return (
    <>
      {text.split('\n').map((line, i) => (
        <span key={i} className="block min-h-[0.35rem]">{renderInline(line, i)}</span>
      ))}
    </>
  );
}

const STARTERS = [
  'How do I add a beneficiary?',
  'Where can I leave a message for my family?',
  'What is the Credentials Vault?',
];

export default function NovaAssistant() {
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [streaming, setStreaming] = useState(false);
  const scrollRef = useRef(null);
  const sessionId = useRef(localStorage.getItem('nova_session_id') || '');

  // Ensure a stable session id
  if (!sessionId.current) {
    sessionId.current = `nova_${Math.random().toString(36).slice(2)}_${Date.now()}`;
    localStorage.setItem('nova_session_id', sessionId.current);
  }

  const token = localStorage.getItem('session_token');
  const shouldShow = !!token && !HIDDEN_PATHS.includes(location.pathname);

  const scrollToBottom = useCallback(() => {
    requestAnimationFrame(() => {
      if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    });
  }, []);

  // Load history the first time the panel opens
  useEffect(() => {
    if (open && messages.length === 0) {
      (async () => {
        try {
          const res = await fetch(`${BACKEND_URL}/api/assistant/history?session_id=${encodeURIComponent(sessionId.current)}`, {
            headers: { Authorization: `Bearer ${token}` },
          });
          const data = await res.json();
          if (data.messages?.length) setMessages(data.messages.map((m) => ({ role: m.role, content: m.content })));
        } catch (e) { /* ignore */ }
      })();
    }
  }, [open]);

  useEffect(() => { scrollToBottom(); }, [messages, streaming, scrollToBottom]);

  const sendMessage = async (text) => {
    const message = (text ?? input).trim();
    if (!message || streaming) return;
    setInput('');
    setMessages((prev) => [...prev, { role: 'user', content: message }, { role: 'assistant', content: '' }]);
    setStreaming(true);

    try {
      const res = await fetch(`${BACKEND_URL}/api/assistant/chat`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ session_id: sessionId.current, message }),
      });

      if (!res.ok || !res.body) throw new Error('Request failed');

      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const events = buffer.split('\n\n');
        buffer = events.pop() || '';
        for (const evt of events) {
          const line = evt.split('\n').find((l) => l.startsWith('data: '));
          if (!line) continue;
          try {
            const payload = JSON.parse(line.slice(6));
            if (payload.delta) {
              setMessages((prev) => {
                const next = [...prev];
                next[next.length - 1] = { role: 'assistant', content: next[next.length - 1].content + payload.delta };
                return next;
              });
            } else if (payload.error) {
              setMessages((prev) => {
                const next = [...prev];
                next[next.length - 1] = { role: 'assistant', content: payload.error };
                return next;
              });
            }
          } catch (e) { /* ignore parse errors */ }
        }
      }
    } catch (e) {
      setMessages((prev) => {
        const next = [...prev];
        next[next.length - 1] = { role: 'assistant', content: 'Sorry, I had trouble connecting. Please try again in a moment. 🤍' };
        return next;
      });
    } finally {
      setStreaming(false);
    }
  };

  if (!shouldShow) return null;

  return (
    <>
      {/* Floating diamond bubble */}
      <AnimatePresence>
        {!open && (
          <motion.button
            data-testid="nova-bubble-button"
            onClick={() => setOpen(true)}
            className="fixed bottom-5 right-5 z-[60] w-16 h-16 rounded-full bg-white shadow-2xl shadow-[#2B4C7E]/40 border-2 border-[#EBF0F5] flex items-center justify-center group"
            initial={{ scale: 0, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0, opacity: 0 }}
            whileHover={{ scale: 1.08 }}
            whileTap={{ scale: 0.94 }}
            aria-label="Open Nova assistant"
          >
            {/* Pulsing glow ring */}
            <motion.span
              className="absolute inset-0 rounded-full bg-gradient-to-br from-[#3A5C8F]/30 via-[#D0B384]/30 to-[#3A5C8F]/30"
              animate={{ scale: [1, 1.25, 1], opacity: [0.7, 0, 0.7] }}
              transition={{ duration: 2.2, repeat: Infinity, ease: 'easeInOut' }}
            />
            {/* Floating diamond */}
            <motion.div
              animate={{ y: [0, -3, 0] }}
              transition={{ duration: 2.4, repeat: Infinity, ease: 'easeInOut' }}
            >
              <AppIcon size={42} />
            </motion.div>
            <span className="absolute -top-1 -right-1 w-5 h-5 rounded-full bg-gradient-to-br from-[#2B4C7E] to-[#C8A97E] flex items-center justify-center shadow">
              <Sparkles className="h-3 w-3 text-white" />
            </span>
          </motion.button>
        )}
      </AnimatePresence>

      {/* Chat panel */}
      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="nova-chat-panel"
            className="fixed bottom-5 right-5 z-[60] w-[calc(100vw-2.5rem)] sm:w-[380px] h-[70vh] sm:h-[560px] max-h-[640px] bg-white rounded-3xl shadow-2xl shadow-[#2B4C7E]/30 border border-[#EBF0F5] flex flex-col overflow-hidden"
            initial={{ opacity: 0, y: 30, scale: 0.92 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 30, scale: 0.92 }}
            transition={{ type: 'spring', stiffness: 260, damping: 24 }}
          >
            {/* Header */}
            <div className="px-4 py-3 bg-gradient-to-r from-[#2B4C7E] via-[#B3956B] to-[#2B4C7E] text-white flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-white/15 backdrop-blur flex items-center justify-center">
                  <AppIcon size={30} />
                </div>
                <div>
                  <h3 className="font-bold leading-tight">Nova</h3>
                  <p className="text-[11px] text-white/80 leading-tight">Your Easy Passing guide</p>
                </div>
              </div>
              <button
                data-testid="nova-close-button"
                onClick={() => setOpen(false)}
                className="w-8 h-8 rounded-full hover:bg-white/20 flex items-center justify-center transition-colors"
                aria-label="Close Nova"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            {/* Messages */}
            <div ref={scrollRef} className="flex-1 overflow-y-auto px-4 py-4 space-y-3 bg-gradient-to-b from-[#EBF0F5]/40 to-white">
              {messages.length === 0 && (
                <div className="text-center py-6">
                  <motion.div
                    className="inline-flex"
                    animate={{ y: [0, -4, 0] }}
                    transition={{ duration: 2.4, repeat: Infinity, ease: 'easeInOut' }}
                  >
                    <AppIcon size={56} />
                  </motion.div>
                  <p className="mt-3 text-gray-700 font-semibold">Hi, I'm Nova ✨</p>
                  <p className="text-sm text-gray-500 px-4 mt-1">I'm here to help you find your way around Easy Passing — and to gently support you along the way.</p>
                  <div className="mt-4 space-y-2">
                    {STARTERS.map((s) => (
                      <button
                        key={s}
                        data-testid="nova-starter-prompt"
                        onClick={() => sendMessage(s)}
                        className="block w-full text-left text-sm px-3 py-2 rounded-xl bg-white border border-[#EBF0F5] text-[#1E3A63] hover:bg-[#EBF0F5] hover:border-[#D7E0EA] transition-colors shadow-sm"
                      >
                        {s}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {messages.map((m, i) => (
                <div key={i} className={`flex ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                  <div
                    data-testid={m.role === 'user' ? 'nova-user-message' : 'nova-assistant-message'}
                    className={`max-w-[85%] px-3.5 py-2.5 rounded-2xl text-sm leading-relaxed ${
                      m.role === 'user'
                        ? 'bg-gradient-to-br from-[#2B4C7E] to-[#B3956B] text-white rounded-br-md'
                        : 'bg-white border border-gray-100 text-gray-700 rounded-bl-md shadow-sm'
                    }`}
                  >
                    {m.role === 'assistant' && m.content === '' && streaming ? (
                      <span className="flex gap-1 py-1">
                        {[0, 1, 2].map((d) => (
                          <motion.span
                            key={d}
                            className="w-2 h-2 rounded-full bg-[#3A5C8F]"
                            animate={{ y: [0, -4, 0] }}
                            transition={{ duration: 0.8, repeat: Infinity, delay: d * 0.15 }}
                          />
                        ))}
                      </span>
                    ) : (
                      <FormattedText text={m.content} />
                    )}
                  </div>
                </div>
              ))}
            </div>

            {/* Input */}
            <div className="p-3 border-t border-gray-100 bg-white">
              <form
                onSubmit={(e) => { e.preventDefault(); sendMessage(); }}
                className="flex items-end gap-2"
              >
                <textarea
                  data-testid="nova-input"
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendMessage(); }
                  }}
                  rows={1}
                  placeholder="Ask Nova anything..."
                  className="flex-1 resize-none max-h-24 px-3.5 py-2.5 rounded-2xl border border-gray-200 focus:border-[#B9C7D6] focus:ring-2 focus:ring-[#EBF0F5] outline-none text-sm text-gray-700 placeholder-gray-400"
                />
                <motion.button
                  data-testid="nova-send-button"
                  type="submit"
                  disabled={!input.trim() || streaming}
                  whileTap={{ scale: 0.9 }}
                  className="w-10 h-10 shrink-0 rounded-full bg-gradient-to-br from-[#2B4C7E] to-[#B3956B] text-white flex items-center justify-center disabled:opacity-40 disabled:cursor-not-allowed shadow-md"
                  aria-label="Send message"
                >
                  <Send className="h-4 w-4" />
                </motion.button>
              </form>
              <p className="text-[10px] text-gray-400 text-center mt-1.5">Nova can make mistakes. For legal or financial advice, consult a professional.</p>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
